import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  EditableMarkdownField, EditablePhoto, EditableText, useSectionEditing, useTemplateVariables,
  type ImageValue,
} from '@kevlarcon/pages';
import { fetchStateDetailMap } from '../../api/states';
import { useStateMap } from '../../hooks/useStateMap';
import StateMap from '../StateMap';

type StateTemplatePageProps = {
  eyebrow?: string;
  heading: string;
  lede?: string;
  /** Text beside the figure, e.g. `{{state_history}}` resolved server-side
   * for this state. When it has any, the figure insets like an image next to
   * it; empty (a state without a history) keeps the figure full-width. */
  body?: string;
  /** Picture shown in place of the map; unset, the map is the fallback. A saved picture, or — bound to a
   * per-state variable — `{url, alt}` on the public page and the lone token while editing. */
  image?: ImageValue | string;
  /** Shown under the image (not the map); like the image and alt text, usually a per-state variable. */
  caption?: string;
};

export const STATE_PATH = /^\/pages\/state\/([a-z]{2})\/?$/i;

/**
 * The per-state figure behind a state's PageTemplate page (see
 * `tenant_site.page_templates.StatePageTemplate`): an admin-chosen `image`,
 * or — when there isn't one — the state's coverage map. Same highlight rules
 * as the homepage States section — both draw through `StateMap`, whose
 * REGISTERED/IN_DEVELOPMENT/NEIGHBORING fill layers key off the same
 * `sgo_registered`/`opted_in` booleans — this just asks the server to frame
 * and return the one state named by the current route instead of the
 * tenant's whole footprint (`GET /api/states/<abbr>/map/`, see
 * `api/states.ts`'s `fetchStateDetailMap`). The state is fetched either way:
 * its callout captions the image too.
 *
 * Which state to draw comes from the URL, not a stored field: like States'
 * entire map, it isn't admin-authored content (see
 * `tenant_site/section_types.py`'s `STATE_TEMPLATE_PAGE`). Heading can carry
 * a `{{full_name}}` token, resolved server-side per state (see
 * `pages.template_rendering`).
 *
 * Partner pins are not drawn here — that's the separate `PartnerMap` section.
 *
 * `body` switches the layout: text on one side, the figure framed on the
 * other as an inset plate with its callout as the caption. Editors always get
 * the inset layout, so there is somewhere to write the text.
 */
export default function StateTemplatePage({
  eyebrow, heading, lede, body, image, caption,
}: StateTemplatePageProps) {
  const { active, background } = useSectionEditing();
  const location = useLocation();

  const abbreviation = useMemo(
    () => location.pathname.match(STATE_PATH)?.[1],
    [location.pathname],
  );
  const request = useStateMap(
    (signal) =>
      abbreviation
        ? fetchStateDetailMap(abbreviation, signal)
        : Promise.reject(new Error('StateTemplatePage rendered outside a /pages/state/<abbr>/ route')),
    [abbreviation],
  );

  const mapData = request.status === 'ready' ? request.data : undefined;
  const feature = mapData?.features[0];
  const inset = Boolean(body?.trim()) || active;
  // Edit mode hands this section its raw fields, so a `{{state_image}}` token must be resolved
  // here: a state with no image of its own keeps the map instead of an empty frame.
  const { rawValues } = useTemplateVariables();
  const resolvedImage = typeof image === 'string' ? (rawValues[image.replace(/[{}\s]/g, '')] as ImageValue | undefined) : image;
  const hasImage = Boolean(resolvedImage?.url?.trim());

  const picture = hasImage ? (
    <div className="state-figure-image" data-testid="state-template-page-image">
      <EditablePhoto
        imagePath={['image']}
        image={image}
        maxDimensions={{ width: 2400, height: 2400 }}
      />
    </div>
  ) : mapData && feature ? (
    <StateMap
      data={mapData}
      testId="state-template-page-map"
      ariaLabel={`Map of ${feature.properties.full_name}`}
      background={background}
    />
  ) : (
    <div className="state-map-canvas state-map-placeholder" data-testid="state-template-page-map-placeholder">
      <span>
        {request.status === 'error'
          ? 'The map is unavailable right now.'
          : request.status === 'missing'
            ? 'This state does not currently have a page.'
            : 'Loading map…'}
      </span>
    </div>
  );

  const figure = (
    <div className="state-map-wrap">
      {picture}
      {hasImage && (caption || active) && (
        <EditableText
          as="div"
          className="state-figure-caption"
          kind="text"
          value={caption ?? ''}
          path={['caption']}
        />
      )}
      {feature && (
        <div className="map-callout" aria-live="polite">
          <div className="map-callout-code">{feature.properties.abbreviated_name}</div>
          {feature.properties.sgo_registered ? (
            <div className="map-callout-status">Active program</div>
          ) : feature.properties.opted_in ? (
            <div className="map-callout-status">In development</div>
          ) : null}
          <div className="map-callout-meta">{feature.properties.full_name}</div>
        </div>
      )}
    </div>
  );

  return (
    <section className={`states section${inset ? ' states--inset' : ''}`}>
      <div className="wrap">
        <div className="head">
          {(eyebrow || active) && (
            <EditableText as="div" className="eyebrow" kind="text" value={eyebrow ?? ''} path={['eyebrow']} />
          )}
          <EditableText as="h2" kind="richtext" value={heading} path={['heading']} />
          {(lede || active) && (
            <EditableMarkdownField value={lede ?? ''} path={['lede']} />
          )}
        </div>

        {/* With no image set there's nothing on the page to click to add
            one — the map isn't an EditablePhoto — so editors get this
            standalone picker. Once set, the image itself is clickable too. */}
        {active && (
          <div className="partner-map-config" data-testid="state-template-page-image-config">
            <label>
              {hasImage ? 'Image (open it to change it or go back to the map)' : 'Image instead of the map'}
              <span className="state-figure-image-thumb">
                <EditablePhoto
                  imagePath={['image']}
                  image={image}
                  maxDimensions={{ width: 2400, height: 2400 }}
                />
              </span>
            </label>
          </div>
        )}

        {inset ? (
          <div className="state-inset" data-testid="state-template-page-inset">
            <div className="state-inset-text">
              <EditableMarkdownField value={body ?? ''} path={['body']} />
            </div>
            <figure className="state-inset-figure">{figure}</figure>
          </div>
        ) : figure}
      </div>
    </section>
  );
}
